
import React from 'react';
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Eye, EyeOff } from "lucide-react";

interface TimelineElement {
  id: string;
  name: string;
  type: 'rrweb' | 'slide' | 'audio';
  startTime: number;
  duration: number;
  color: string;
}

interface TimelineTrackProps {
  title: string;
  elements: TimelineElement[];
  pixelsPerSecond: number;
  onElementDrag: (elementId: string, startDrag: boolean) => void;
}

const TimelineTrack = ({ title, elements, pixelsPerSecond, onElementDrag }: TimelineTrackProps) => {
  const [isVisible, setIsVisible] = React.useState(true);
  const [selectedElement, setSelectedElement] = React.useState<string | null>(null);

  const handleElementMouseDown = (elementId: string) => {
    setSelectedElement(elementId);
    onElementDrag(elementId, true);
  };

  const handleElementMouseUp = (elementId: string) => {
    onElementDrag(elementId, false);
  };

  return (
    <div className="border-b border-editor-light">
      {/* Track Header */}
      <div className="h-8 bg-editor-medium flex items-center px-4 border-b border-editor-light">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsVisible(!isVisible)}
          className="p-1 mr-2"
        >
          {isVisible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
        </Button>
        <span className="text-sm font-medium text-gray-300 flex-1">{title}</span>
        <span className="text-xs text-gray-500 mr-2">{elements.length}</span>
        <Button variant="ghost" size="sm" className="p-1">
          <MoreHorizontal className="h-3 w-3" />
        </Button>
      </div>

      {/* Track Content */}
      {isVisible && (
        <div className="h-16 bg-editor-dark relative overflow-hidden">
          {elements.map((element) => (
            <div
              key={element.id}
              className={`absolute top-2 h-12 rounded cursor-move select-none transition-all ${element.color} ${
                selectedElement === element.id ? 'ring-2 ring-editor-accent' : ''
              }`}
              style={{
                left: `${(element.startTime / 1000) * pixelsPerSecond}px`,
                width: `${Math.max(40, (element.duration / 1000) * pixelsPerSecond)}px`
              }}
              onMouseDown={() => handleElementMouseDown(element.id)}
              onMouseUp={() => handleElementMouseUp(element.id)}
              title={`${element.name} (${(element.duration / 1000).toFixed(1)}s)`}
            >
              <div className="p-2 h-full flex items-center">
                <div className="text-white text-xs font-medium truncate">
                  {element.name}
                </div>
              </div>
              
              {/* Resize handles */}
              <div className="absolute left-0 top-0 w-1 h-full bg-white/20 cursor-w-resize opacity-0 hover:opacity-100" />
              <div className="absolute right-0 top-0 w-1 h-full bg-white/20 cursor-e-resize opacity-0 hover:opacity-100" />
            </div>
          ))}
          
          {elements.length === 0 && (
            <div className="h-full flex items-center justify-center text-gray-500 text-xs">
              No {title.toLowerCase()} elements
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineTrack;
